import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { PawPrint, ShieldCheck, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/lib/i18n";

const DEMOS = [
  { role: "admin",   label: "Admin",   email: "admin@ohmydog.com",   password: "Demo123!" },
  { role: "trainer", label: "Trainer", email: "trainer@ohmydog.com", password: "Demo123!" },
  { role: "parent",  label: "Parent",  email: "parent@ohmydog.com",  password: "Demo123!" },
];

export default function Login() {
  const { login } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [activeRole, setActiveRole] = useState(null);
  const [copied, setCopied] = useState(null);

  const fillDemo = (d) => {
    setActiveRole(d.role);
    setEmail(d.email);
    setPassword(d.password);
    setError("");
  };

  const copy = async (kind, value) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(kind);
      setTimeout(() => setCopied(null), 1500);
    } catch {}
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const u = await login(email, password);
      toast.success(`Welcome, ${u.name}!`);
      const target = loc.state?.from || `/${u.role}`;
      nav(target, { replace: true });
    } catch (err) {
      const msg = formatApiError(err.response?.data?.detail) || err.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const active = DEMOS.find((d) => d.role === activeRole);

  return (
    <div className="min-h-[80vh] grid lg:grid-cols-2 max-w-7xl mx-auto px-4 sm:px-6 py-10 gap-8 items-center">
      {/* DESKTOP marketing panel */}
      <div className="hidden lg:block">
        <div className="rounded-[2rem] gradient-hero p-10 paw-pattern relative overflow-hidden">
          <PawPrint className="w-12 h-12 text-primary mb-4 wiggle" />
          <h2 className="text-4xl font-black tracking-tight mb-3 leading-tight">{t("login.title")}</h2>
          <p className="text-muted-foreground mb-6 max-w-sm">{t("login.sub")}</p>
          <div className="rounded-2xl bg-card border border-border/60 p-5 soft-shadow">
            <p className="text-xs font-bold uppercase tracking-widest text-primary mb-3">{t("login.demo")}</p>
            <div className="space-y-2 text-sm">
              {DEMOS.map((d) => (
                <div key={d.role} className="flex items-center justify-between">
                  <span className="font-bold capitalize w-20">{d.role}</span>
                  <span className="text-muted-foreground text-xs">{d.email}</span>
                </div>
              ))}
              <div className="border-t border-border/60 pt-2 flex items-center justify-between">
                <span className="font-bold w-20">Password</span>
                <code className="text-primary font-bold">Demo123!</code>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* SIGN IN CARD (mobile-friendly) */}
      <Card className="rounded-3xl border-border/60 soft-shadow">
        <CardContent className="p-6 sm:p-10">
          <h1 className="text-3xl font-black tracking-tight mb-1">Sign in</h1>
          <p className="text-muted-foreground mb-6">Use your Oh My Dog! account.</p>

          {/* DEMO ROLE TOGGLE — visible on ALL screen sizes */}
          <div className="rounded-2xl bg-muted/40 border border-border/60 p-4 mb-6" data-testid="demo-toggle">
            <p className="text-xs font-bold uppercase tracking-widest text-primary mb-3 flex items-center gap-1.5">
              <ShieldCheck className="w-3.5 h-3.5" /> Demo accounts · tap to fill
            </p>
            <div className="grid grid-cols-3 gap-2 mb-3">
              {DEMOS.map((d) => (
                <button
                  key={d.role}
                  type="button"
                  onClick={() => fillDemo(d)}
                  className={`px-3 py-2 rounded-full text-sm font-bold transition ${
                    activeRole === d.role
                      ? "bg-primary text-primary-foreground shadow-md"
                      : "bg-card border border-border/60 hover:border-primary"
                  }`}
                  data-testid={`demo-fill-${d.role}`}
                >
                  {d.label}
                </button>
              ))}
            </div>
            {active ? (
              <div className="space-y-1.5 text-sm" data-testid="demo-active-info">
                <div className="flex items-center justify-between gap-2 bg-card rounded-xl px-3 py-2 border border-border/60">
                  <div className="min-w-0">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Email</p>
                    <p className="font-bold truncate">{active.email}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => copy("email", active.email)}
                    className="p-2 rounded-full hover:bg-primary/10 transition shrink-0"
                    aria-label="Copy email"
                    data-testid="copy-email"
                  >
                    {copied === "email" ? <Check className="w-4 h-4 text-primary" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
                <div className="flex items-center justify-between gap-2 bg-card rounded-xl px-3 py-2 border border-border/60">
                  <div className="min-w-0">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Password</p>
                    <p className="font-bold">{active.password}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => copy("password", active.password)}
                    className="p-2 rounded-full hover:bg-primary/10 transition shrink-0"
                    aria-label="Copy password"
                    data-testid="copy-password"
                  >
                    {copied === "password" ? <Check className="w-4 h-4 text-primary" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground italic">
                Pick a role above to auto-fill the form.
              </p>
            )}
          </div>

          <form className="space-y-4" onSubmit={onSubmit} data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-xs font-bold uppercase tracking-wider">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5 rounded-xl"
                required
                data-testid="login-email-input"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs font-bold uppercase tracking-wider">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5 rounded-xl"
                required
                data-testid="login-password-input"
              />
            </div>
            {error && <p className="text-sm text-destructive" data-testid="login-error">{error}</p>}
            <Button type="submit" disabled={busy} className="w-full rounded-full font-bold" data-testid="login-submit-btn">
              {busy ? "Signing in…" : "Sign in"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
